// 끼 종목(Stock) 화면 공용 타입·포맷터·표시 상수 — eztrade /kki 화면 규칙을 그대로 옮김

export interface KkiDay {
  d: string;   // YYYY-MM-DD
  v: number;   // 그날 거래대금
  r: number;   // 그날 상승률(%)
}

export interface KkiRow {
  code: string;
  name: string;
  market: string;          // kospi | kosdaq
  sector: string;
  kki: number;             // 끼 횟수
  g1_min: number | null;   // 끼분 (분봉 미수집 종목은 null)
  marcap: number | null;
  value: number | null;    // 당일 거래대금
  avg_value: number | null;
  avg_rise: number | null;
  chg: number | null;      // 당일 등락률
  r1w: number | null; r2w: number | null; r1m: number | null;
  r2m: number | null; r3m: number | null; r6m: number | null;
  dd: number | null;       // 1년(250거래일) 최고가 대비 하락률 — 항상 0 이하
  du: number | null;       // 3개월(66거래일) 최저가 대비 상승률 — 항상 0 이상
  days: KkiDay[];          // 끼 발생일 상세 (팝업용 — 추가 조회 없음)
}

export interface KkiSector {
  name: string;
  count: number;
  marcap: number | null;
  kki: number | null;
  g1_min: number | null;
  g1n: number;             // 끼분 값이 있는 종목 수
  value: number | null;
  chg: number | null;
  chg_top3: number | null; // 당일 상위 3종목 평균
  r1w: number | null; r2w: number | null; r1m: number | null;
  r2m: number | null; r3m: number | null; r6m: number | null;
  dd: number | null;
  empty?: boolean;         // 종목 표에 하나도 안 잡힌 섹터
}

export interface KkiResponse {
  rows: KkiRow[];
  sectors: KkiSector[];
  asof: string | null;
  prev: string | null;
  g1_date: string | null;  // YYYYMMDD — 끼분 기준일(일봉보다 하루 앞설 수 있음)
  fav_sectors: string[];
  favs: string[];
  criteria: { min_cap: number; min_days: number; days: number; value: number; body_pct: number };
}

export interface SearchRow {
  code: string; name: string; market: string; sector: string;
}

// ── 포맷터 — eztrade detail.js 와 같은 규약 (거래대금은 항상 억, 시총은 항상 조) ──
export const N = (v: number | null | undefined, d = 0): string =>
  typeof v === "number"
    ? v.toLocaleString("ko-KR", { minimumFractionDigits: d, maximumFractionDigits: d })
    : "-";
export const EOK = (v: number | null | undefined): string =>
  typeof v === "number" && v > 0 ? `${N(v / 1e8)}억` : "-";
export const JO = (v: number | null | undefined): string =>
  typeof v === "number" && v > 0 ? `${(v / 1e12).toFixed(2)}조` : "-";
export const pct = (v: number | null | undefined, d = 1): string =>
  typeof v === "number" ? `${v > 0 ? "+" : ""}${v.toFixed(d)}%` : "-";
/** 부호별 글자색 — 상승 빨강 · 하락 파랑 (국내 관례) */
export const sign = (v: number | null | undefined): string =>
  typeof v !== "number" || v === 0 ? "" : v > 0 ? "text-[#e30000]" : "text-[#0059d1]";

// ── 표시 상수 (사용자가 지정해 온 값 — 임의 변경 금지, context-notes 참고) ──
/** 이 값 이상이면 오렌지로 띄운다 (섹터=당일상위3종목 · 종목=당일등락률) */
export const HOT_PCT = 10;
/** 섹터 전체 평균도 이만큼 올랐고 HOT 이면 행 아래 밑줄 */
export const LEAD_CHG = 5;
/** 끼합이 이 값 이하인 섹터 행은 회색 바탕 + 정렬 시 아래 묶음 */
export const LOWK_MAX = 2;
/** 기간수익률(1w·1m) 막대 색 — 당일 열보다 옅어야 한다 */
export const RET_BC = "rgba(249,115,22,.45)";
export const HOT_BC = "rgba(249,115,22,.9)";
export const GOLD_BC = "rgba(255,193,7,.9)";
export const NEG_BC = "rgba(0,89,209,.35)";

export const DD_TIP =
  "최근 1년(250거래일) 최고가 대비 하락률 — 고점에서 얼마나 내려와 있나\n" +
  "종가가 아니라 고가의 최대값 기준입니다\n" +
  "섹터 표는 소속 종목 DD 의 평균입니다";
/** DD 의 대칭 — 종목 표에만 둔다 (섹터 집계에는 du 가 없다) */
export const DU_TIP =
  "최근 3개월(66거래일) 최저가 대비 상승률 — 바닥에서 얼마나 올라와 있나\n" +
  "DD 와 대칭입니다 (종가가 아니라 저가의 최소값 기준)\n" +
  "클수록 이미 많이 오른 자리, 작을수록 바닥권입니다";

export const MKNAME: Record<string, string> = { kospi: "코스피", kosdaq: "코스닥" };

// ── 끼 판정 (eztrade detail.js 와 같은 잣대 — 끼 화면·종목 차트가 같은 기준을 써야 한다) ──
/** 끼: 거래대금 1,000억 이상 */
export const STRONG_VALUE = 1000e8;
/** 끼: 시가 대비 종가 10% 이상 */
export const STRONG_BODY = 10;
/** 그 일봉이 '끼'인가 — 거래대금(원)과 시가·종가로 판정 */
export const isKkiDay = (open: number, close: number, value: number): boolean =>
  value >= STRONG_VALUE && open > 0 && close >= open * (1 + STRONG_BODY / 100);

/** 값이 없는 행은 정렬 방향과 무관하게 항상 맨 아래 */
export function cmp(x: unknown, y: unknown, numeric: boolean, desc: boolean): number {
  if (numeric) {
    const nx = x == null ? -Infinity : (x as number);
    const ny = y == null ? -Infinity : (y as number);
    const v = nx === ny ? 0 : nx < ny ? -1 : 1;
    return desc ? -v : v;
  }
  const v = String(x ?? "").localeCompare(String(y ?? ""), "ko");
  return desc ? -v : v;
}

/** 셀 배경 막대 스타일 — ::before 가 opacity:.5 를 한 번 더 곱한다 */
export function barStyle(width: number, color?: string): React.CSSProperties {
  return { ["--w" as string]: `${Math.min(100, Math.max(0, width))}%`, ...(color ? { ["--bc" as string]: color } : {}) };
}
